"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { normalizeLinkPreviewFeaturedImageUrl } from "./normalizeLinkPreviewFeaturedImageUrl";
import type { BuilderInterlinkLinkPreview } from "./types";
export type BuilderInterlinkPreviewCardProps = Pick<BuilderInterlinkLinkPreview, "title" | "excerpt" | "urlPath" | "featuredImageUrl">;
const CARD_WIDTH = "21rem";
const FG = "var(--tt-color-fg, #e6edf3)";
const MUTED = "var(--tt-color-muted, #21262d)";
const ACCENT = "var(--tt-color-primary, var(--tt-color-accent, #38bdf8))";
/** Text body padding — inline so Region Instance works without scanning builder Tailwind. */
const TEXT_PAD = "0.875rem 1rem 1rem";
const lineClamp2 = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: 2,
  overflow: "hidden"
};

/**
 * Collection / type label from the path segment before the slug.
 * Skips `/{region}/{slug}` (deployment prefix only) so kickers like "Us" are not shown.
 */
function kickerFromUrlPath(urlPath: string): string | null {
  const parts = urlPath.split("/").filter(part => part.trim().length > 0);
  if (parts.length < 3) {
    return null;
  }
  const segment = parts[parts.length - 2];
  if (!segment) {
    return null;
  }
  const words = decodeURIComponent(segment).replace(/[-_]+/g, " ").trim();
  return words ? words.replace(/\b\w/g, c => c.toUpperCase()) : null;
}

/**
 * Skeleton placeholder shown while a hover preview's data is loading. Shares
 * {@link BuilderInterlinkPreviewCard}'s box model so there is no layout jump
 * when the real content swaps in.
 */
export function BuilderInterlinkPreviewSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div style={{
      width: CARD_WIDTH,
      maxWidth: "calc(100vw - 2rem)"
    }}><div style={{
        width: "100%",
        aspectRatio: "16 / 9",
        backgroundColor: MUTED,
        animation: "pulse 1.5s ease-in-out infinite",
        opacity: 0.7
      }} /><div style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        padding: TEXT_PAD
      }}><div style={{
          height: "0.625rem",
          width: "40%",
          borderRadius: "9999px",
          backgroundColor: MUTED,
          opacity: 0.7
        }} /><div style={{
          height: "0.75rem",
          width: "80%",
          borderRadius: "9999px",
          backgroundColor: MUTED,
          opacity: 0.7
        }} /><div style={{
          height: "0.625rem",
          width: "100%",
          borderRadius: "9999px",
          backgroundColor: MUTED,
          opacity: 0.7
        }} /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Rich hover card for interlink previews (builder canvas + host shells):
 * featured image, optional kicker, SEO title, meta description, and footer.
 * Layout uses inline styles so the card renders correctly on public Region pages
 * (builder package paths are not always in the app Tailwind `@source` scan).
 */
export function BuilderInterlinkPreviewCard(t0) {
  const $ = _c(35);
  const {
    title,
    excerpt,
    urlPath,
    featuredImageUrl
  } = t0;
  const [imageFailed, setImageFailed] = useState(false);
  let t1;
  if ($[0] !== featuredImageUrl) {
    t1 = normalizeLinkPreviewFeaturedImageUrl(featuredImageUrl);
    $[0] = featuredImageUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const imageUrl = t1;
  const showImage = Boolean(imageUrl) && !imageFailed;
  let t2;
  if ($[2] !== title) {
    t2 = title?.trim();
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasTitle = Boolean(t2);
  let t3;
  if ($[4] !== excerpt) {
    t3 = excerpt?.trim();
    $[4] = excerpt;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasExcerpt = Boolean(t3);
  let hasUrlPath;
  let t4;
  let trimmedUrlPath;
  if ($[6] !== urlPath) {
    trimmedUrlPath = urlPath?.trim() ?? "";
    hasUrlPath = trimmedUrlPath.length > 0;
    t4 = hasUrlPath ? kickerFromUrlPath(trimmedUrlPath) : null;
    $[6] = urlPath;
    $[7] = hasUrlPath;
    $[8] = t4;
    $[9] = trimmedUrlPath;
  } else {
    hasUrlPath = $[7];
    t4 = $[8];
    trimmedUrlPath = $[9];
  }
  const kicker = t4;
  const imageAlt = hasTitle ? `${title} featured image` : "Featured image";
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      width: CARD_WIDTH,
      maxWidth: "calc(100vw - 2rem)"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== imageAlt || $[12] !== imageUrl || $[13] !== showImage) {
    t6 = showImage ? <div style={{
      position: "relative",
      width: "100%",
      overflow: "hidden",
      aspectRatio: "16 / 9",
      backgroundColor: MUTED
    }}><img alt={imageAlt} decoding="async" loading="lazy" onError={() => setImageFailed(true)} src={imageUrl as string} style={{
        display: "block",
        height: "100%",
        width: "100%",
        maxHeight: "100%",
        maxWidth: "100%",
        objectFit: "cover"
      }} /><div aria-hidden="true" style={{
        pointerEvents: "none",
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "2rem",
        background: "linear-gradient(to bottom, transparent, color-mix(in srgb, var(--tt-color-bg, #161b22) 60%, transparent))"
      }} /></div> : null;
    $[11] = imageAlt;
    $[12] = imageUrl;
    $[13] = showImage;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
      padding: TEXT_PAD
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== kicker) {
    t8 = kicker ? <p style={{
      margin: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontWeight: 600,
      textTransform: "uppercase",
      lineHeight: 1,
      fontSize: "0.6875rem",
      letterSpacing: "0.06em",
      color: ACCENT
    }}>{kicker}</p> : null;
    $[16] = kicker;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== hasTitle || $[19] !== title) {
    t9 = hasTitle ? <p style={{
      margin: 0,
      fontWeight: 600,
      lineHeight: 1.35,
      fontSize: "var(--tt-text-sm, 0.9375rem)",
      color: FG,
      ...lineClamp2
    }}>{title}</p> : null;
    $[18] = hasTitle;
    $[19] = title;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== excerpt || $[22] !== hasExcerpt) {
    t10 = hasExcerpt ? <p style={{
      margin: 0,
      lineHeight: 1.5,
      opacity: 0.7,
      fontSize: "var(--tt-text-xs, 0.8125rem)",
      color: FG,
      ...lineClamp2
    }}>{excerpt}</p> : null;
    $[21] = excerpt;
    $[22] = hasExcerpt;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== hasUrlPath || $[25] !== trimmedUrlPath) {
    t11 = hasUrlPath ? <div style={{
      marginTop: "0.25rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "0.5rem",
      paddingTop: "0.625rem",
      borderTop: "1px solid color-mix(in srgb, var(--tt-color-fg, #e6edf3) 10%, transparent)"
    }}><span style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        opacity: 0.5,
        fontSize: "0.6875rem",
        color: FG
      }}>{trimmedUrlPath}</span><span style={{
        display: "flex",
        flexShrink: 0,
        alignItems: "center",
        gap: "0.25rem",
        fontWeight: 600,
        fontSize: "0.75rem",
        color: ACCENT
      }}>Read<svg aria-hidden="true" fill="none" height="12" viewBox="0 0 24 24" width="12"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg></span></div> : null;
    $[24] = hasUrlPath;
    $[25] = trimmedUrlPath;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t8 || $[30] !== t9) {
    t12 = <div style={t7}>{t8}{t9}{t10}{t11}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t8;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== t6) {
    t13 = <div style={t5}>{t6}{t12}</div>;
    $[32] = t12;
    $[33] = t6;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}